import Reveal from "@/components/feature/Reveal";
import SectionTitle from "@/components/feature/SectionTitle";
import { companyProfile, qualifications, companyStrengths } from "@/mocks/company";
import {
  COMPANY_NAME,
  GOOGLE_REVIEW_URL,
  WEBSITE,
  INSTAGRAM_URL,
  ADDRESS,
} from "@/constants";
import MascotAccent from "@/components/feature/MascotAccent";

const MAP_EMBED =
  "https://www.google.com/maps?q=%E5%9F%BC%E7%8E%89%E7%9C%8C%E6%98%A5%E6%97%A5%E9%83%A8%E5%B8%82%E5%A4%A7%E5%A0%B4207-1&output=embed";

// 会社紹介で使用する画像（QRコード2つを除去したもの）
const SIGNBOARD_IMG =
  "https://public.readdy.ai/ai/img_res/edited_c7afc4b5b7e44430f46c0163c6ca6381_fed96e4d.jpg";

export default function Company() {
  return (
    <section id="company" className="relative overflow-hidden bg-background-100 py-16 md:py-24">
      <MascotAccent variant="cert" className="bottom-10 left-6 hidden h-16 w-16 xl:block" />
      <div className="site-container">
        <Reveal>
          <SectionTitle
            upper="ABOUT US"
            under="単独の不用品回収ブランドではなく、春日部市で実際に建設・リフォーム業を行っている株式会社小林建設が運営しています。"
          >
            {COMPANY_NAME}について
          </SectionTitle>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <Reveal className="min-w-0">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <div className="min-w-0 overflow-hidden rounded-md border border-background-200 bg-background-100/50">
                <img
                  src={SIGNBOARD_IMG}
                  alt="株式会社小林建設の看板（屋根工事・外壁塗装・春日部市内即日対応）"
                  className="mx-auto block h-auto w-full object-cover"
                />
              </div>
              <p className="mt-3 text-center text-xs font-medium text-foreground-500">
                春日部市で営業する小林建設の看板（本業は屋根工事・外壁塗装）
              </p>
              <p className="mt-5 font-heading text-lg font-black leading-snug text-foreground-950">
                「地元で実際に営業している会社だから、安心して家の中を任せたい」
              </p>
              <ul className="mt-5 space-y-2.5">
                {companyStrengths.map((s) => (
                  <li key={s} className="flex items-start gap-2 text-base leading-relaxed text-foreground-700">
                    <i className="ri-checkbox-circle-fill mt-0.5 text-primary-600" />
                    {s}
                  </li>
                ))}
              </ul>

              <div className="mt-5 flex flex-wrap gap-2">
                <a
                  href={WEBSITE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex cursor-pointer items-center gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-2.5 text-sm font-bold text-primary-700 transition-colors hover:bg-primary-100"
                >
                  <i className="ri-global-line text-lg" />
                  <span className="whitespace-nowrap">コーポレートサイト</span>
                </a>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex cursor-pointer items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-bold text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <i className="ri-instagram-line text-lg" />
                  <span className="whitespace-nowrap">Instagram</span>
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={80} className="min-w-0">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h3 className="font-heading text-lg font-black text-foreground-950">会社概要</h3>
              <dl className="mt-4 divide-y divide-background-100">
                {companyProfile.map((row) => (
                  <div key={row.label} className="flex gap-4 py-2.5">
                    <dt className="w-24 shrink-0 text-sm font-bold text-foreground-500">{row.label}</dt>
                    <dd className="min-w-0 break-words text-base font-medium text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <Reveal className="min-w-0">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h3 className="font-heading text-lg font-black text-foreground-950">資格・許可</h3>
              <ul className="mt-4 space-y-3">
                {qualifications.map((q) => (
                  <li key={q.text} className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100">
                      <i className={`${q.icon} text-lg text-primary-700`} />
                    </span>
                    <span className="text-base font-bold text-foreground-800">{q.text}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex items-start gap-3 rounded-md bg-secondary-50 p-4">
                <i className="ri-star-line mt-0.5 text-lg text-secondary-700" />
                <div className="min-w-0">
                  <p className="text-base font-bold text-secondary-900">運営会社の口コミについて</p>
                  <p className="mt-1 text-sm leading-relaxed text-secondary-800">
                    本業のリフォーム事業でいただいた評価（対応が早い・説明が分かりやすい 等）は、Googleマップの口コミでご確認いただけます。
                  </p>
                  <a
                    href={GOOGLE_REVIEW_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-md bg-secondary-600 px-4 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-secondary-700 sm:inline-flex sm:w-auto"
                  >
                    <i className="ri-google-fill text-lg" />
                    <span className="text-center">Googleマップの口コミを見る</span>
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={80} className="min-w-0">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <iframe
                title="株式会社小林建設の所在地"
                src={MAP_EMBED}
                className="h-[280px] w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <p className="flex items-center gap-2 p-4 text-sm font-bold text-foreground-700">
                <i className="ri-map-pin-2-fill text-primary-600" />
                {ADDRESS}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}